import type { Academy } from '../types';
import type { AcademySetupTaskId } from './operatingProfilePersonalization';

/** Raw existence signals used to derive checklist completion. */
export interface AcademySetupSignals {
  hasCourts: boolean;
  hasCoaches: boolean;
  hasAgeGroups: boolean;
  hasTerms: boolean;
  hasActiveLessons: boolean;
  hasPrograms: boolean;
  hasLocations: boolean;
  hasActiveSports: boolean;
  academy: Pick<
    Academy,
    | 'description'
    | 'phone'
    | 'logoUrl'
    | 'paymentCollectionMessage'
    | 'stripeConnectAccountId'
    | 'onboardingComplete'
    | 'academySports'
  > | null;
  hasPaymentProcessingFeature: boolean;
}

export interface AcademySetupChecklistProgress {
  tasks: Partial<Record<AcademySetupTaskId, string>>;
  reconciledAt?: string;
}

export const ALL_ACADEMY_SETUP_TASK_IDS: readonly AcademySetupTaskId[] = [
  'academy_profile',
  'sports',
  'courts',
  'locations',
  'coaches',
  'age_groups',
  'terms',
  'lessons',
  'programs',
  'payments',
];

export function academyProfileDone(
  academy: AcademySetupSignals['academy']
): boolean {
  if (!academy) return false;
  const desc = academy.description?.trim();
  const phone = academy.phone?.trim();
  const logo = academy.logoUrl?.trim();
  return Boolean(desc || phone || logo);
}

export function paymentsDone(
  academy: AcademySetupSignals['academy'],
  hasPaymentProcessingFeature: boolean
): boolean {
  if (!academy) return false;
  if (hasPaymentProcessingFeature) {
    return Boolean(academy.stripeConnectAccountId && academy.onboardingComplete);
  }
  return Boolean(academy.paymentCollectionMessage?.trim());
}

export function sportsDone(
  academy: AcademySetupSignals['academy'],
  hasActiveSports: boolean
): boolean {
  if (hasActiveSports) return true;
  const sports = academy?.academySports?.filter((s) => s.isActive) ?? [];
  return sports.length > 0;
}

/** Derive completion for all academy setup tasks from raw signals. */
export function deriveAcademySetupTaskDone(
  signals: AcademySetupSignals
): Record<AcademySetupTaskId, boolean> {
  const { academy, hasPaymentProcessingFeature } = signals;

  return {
    academy_profile: academyProfileDone(academy),
    sports: sportsDone(academy, signals.hasActiveSports),
    courts: signals.hasCourts,
    locations: signals.hasLocations,
    coaches: signals.hasCoaches,
    age_groups: signals.hasAgeGroups,
    terms: signals.hasTerms,
    lessons: signals.hasActiveLessons,
    programs: signals.hasPrograms,
    payments: paymentsDone(academy, hasPaymentProcessingFeature),
  };
}

/** Monotonic merge: stored timestamps are never cleared. */
export function mergeSetupProgress(
  stored: AcademySetupChecklistProgress | null | undefined,
  derived: Record<AcademySetupTaskId, boolean>
): { tasks: Partial<Record<AcademySetupTaskId, string>>; changed: boolean } {
  const now = new Date().toISOString();
  const tasks: Partial<Record<AcademySetupTaskId, string>> = {
    ...(stored?.tasks ?? {}),
  };
  let changed = false;

  for (const taskId of ALL_ACADEMY_SETUP_TASK_IDS) {
    if (tasks[taskId]) continue;
    if (derived[taskId]) {
      tasks[taskId] = now;
      changed = true;
    }
  }

  return { tasks, changed };
}

export function isAcademySetupProgressComplete(
  stored: AcademySetupChecklistProgress | null | undefined
): boolean {
  if (!stored?.tasks) return false;
  return ALL_ACADEMY_SETUP_TASK_IDS.every((taskId) => Boolean(stored.tasks[taskId]));
}

/** How long the post-checklist support nudge stays visible before auto-dismiss. */
export const SETUP_SUPPORT_NUDGE_VISIBLE_DAYS = 7;

const SETUP_SUPPORT_NUDGE_VISIBLE_MS =
  SETUP_SUPPORT_NUDGE_VISIBLE_DAYS * 24 * 60 * 60 * 1000;

/** Latest task completion time when every checklist task has a stored timestamp. */
export function resolveAcademySetupChecklistCompletedAt(
  stored: AcademySetupChecklistProgress | null | undefined
): string | null {
  if (!isAcademySetupProgressComplete(stored) || !stored?.tasks) {
    return null;
  }

  let latest: string | null = null;
  for (const taskId of ALL_ACADEMY_SETUP_TASK_IDS) {
    const completedAt = stored.tasks[taskId];
    if (!completedAt) return null;
    if (!latest || completedAt > latest) {
      latest = completedAt;
    }
  }

  return latest;
}

export function isSetupSupportNudgeAutoDismissed(
  completedAt: string,
  now: Date = new Date(),
  visibleMs: number = SETUP_SUPPORT_NUDGE_VISIBLE_MS
): boolean {
  const completedMs = Date.parse(completedAt);
  if (Number.isNaN(completedMs)) {
    return false;
  }

  return now.getTime() - completedMs >= visibleMs;
}

export function progressToDoneMap(
  stored: AcademySetupChecklistProgress | null | undefined
): Record<AcademySetupTaskId, boolean> {
  const tasks = stored?.tasks ?? {};
  const result: Record<AcademySetupTaskId, boolean> = {
    academy_profile: false,
    sports: false,
    courts: false,
    locations: false,
    coaches: false,
    age_groups: false,
    terms: false,
    lessons: false,
    programs: false,
    payments: false,
  };
  for (const taskId of ALL_ACADEMY_SETUP_TASK_IDS) {
    result[taskId] = Boolean(tasks[taskId]);
  }
  return result;
}
